import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { getAdminUsers, getAdminWeddings } from "../../utils/adminApi";
import {
  formatCellValue,
  normalizeRecord,
  pickByKeyPattern,
  pickValue,
} from "./adminValueUtils";

function AdminWeddingsSection() {
  const [weddings, setWeddings] = useState([]);
  const [usersByWeddingId, setUsersByWeddingId] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadWeddings = async () => {
    try {
      setLoading(true);
      setError("");
      const [weddingsData, usersData] = await Promise.all([
        getAdminWeddings(),
        getAdminUsers(),
      ]);
      const safeWeddings = Array.isArray(weddingsData) ? weddingsData : [];
      const safeUsers = Array.isArray(usersData) ? usersData : [];

      const nextUsersByWeddingId = safeUsers.reduce((acc, rawUser) => {
        const user = normalizeRecord(rawUser);
        const userWeddingId = pickValue(user, ["wedding_id", "weddingId"]);
        if (userWeddingId !== null && userWeddingId !== undefined) {
          acc[String(userWeddingId)] = user;
        }
        return acc;
      }, {});

      setUsersByWeddingId(nextUsersByWeddingId);
      setWeddings(safeWeddings);
    } catch (err) {
      console.error(err);
      setError(err.message || "Неуспешно вчитување на свадби.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadWeddings();
  }, []);

  return (
    <Card className="admin-card">
      <CardContent>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={1}
          mb={2}
        >
          <Typography variant="h6">Weddings</Typography>
          <Button variant="outlined" size="small" onClick={loadWeddings}>
            Refresh
          </Button>
        </Stack>

        {error ? <Alert severity="error">{error}</Alert> : null}

        {loading ? (
          <Box className="admin-loading-wrap">
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Box className="admin-table-wrap">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Confirmed</TableCell>
                  <TableCell>Bride</TableCell>
                  <TableCell>Groom</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Venue</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {weddings.map((rawWedding, index) => {
                  const wedding = normalizeRecord(rawWedding);
                  const weddingId = pickValue(wedding, [
                    "wedding_id",
                    "weddingId",
                    "id",
                  ]);
                  const weddingUser = usersByWeddingId[String(weddingId)] || null;
                  const confirmed = pickValue(wedding, [
                    "confirmed",
                    "is_confirmed",
                    "isConfirmed",
                  ]);
                  const brideName = pickValue(wedding, [
                    "bride_name",
                    "brideName",
                  ]) ?? pickValue(weddingUser, ["bride_name", "brideName"]);
                  const brideLastName = pickValue(wedding, [
                    "bride_last_name",
                    "brideLastName",
                  ]) ?? pickValue(weddingUser, ["bride_last_name", "brideLastName"]);
                  const brideFullNameFromParts =
                    [brideName, brideLastName].filter(Boolean).join(" ") || undefined;
                  const bride =
                    brideFullNameFromParts ??
                    pickValue(wedding, [
                    "bride_full_name",
                    "brideFullName",
                    "female_name",
                    "femaleName",
                    "wife_name",
                    "wifeName",
                    "partner1_name",
                    "partner1Name",
                    "bride",
                    ]) ??
                    pickValue(weddingUser, [
                      "bride_full_name",
                      "brideFullName",
                      "female_name",
                      "femaleName",
                    ]) ??
                    pickByKeyPattern(
                      wedding,
                      /(bride|female|wife|partner1|partner_one|nevesta)/i
                    );
                  const groomName = pickValue(wedding, [
                    "groom_name",
                    "groomName",
                  ]) ?? pickValue(weddingUser, ["groom_name", "groomName"]);
                  const groomLastName = pickValue(wedding, [
                    "groom_last_name",
                    "groomLastName",
                  ]) ?? pickValue(weddingUser, ["groom_last_name", "groomLastName"]);
                  const groomFullNameFromParts =
                    [groomName, groomLastName].filter(Boolean).join(" ") || undefined;
                  const groom =
                    groomFullNameFromParts ??
                    pickValue(wedding, [
                    "groom_full_name",
                    "groomFullName",
                    "male_name",
                    "maleName",
                    "husband_name",
                    "husbandName",
                    "partner2_name",
                    "partner2Name",
                    "groom",
                    ]) ??
                    pickValue(weddingUser, [
                      "groom_full_name",
                      "groomFullName",
                      "male_name",
                      "maleName",
                    ]) ??
                    pickByKeyPattern(
                      wedding,
                      /(groom|male|husband|partner2|partner_two|zenik)/i
                    );
                  const date = pickValue(wedding, [
                    "date",
                    "wedding_date",
                    "weddingDate",
                  ]);
                  const venue =
                    pickValue(wedding, [
                      "venue_name",
                      "venueName",
                      "venue",
                      "location",
                      "restaurant_name",
                      "restaurantName",
                      "restaurant",
                      "hall_name",
                      "hallName",
                      "place_name",
                      "placeName",
                    ]) ??
                    pickValue(weddingUser, [
                      "restaurant",
                      "restaurant_name",
                      "restaurantName",
                      "venue",
                      "venue_name",
                      "venueName",
                    ]) ??
                    pickByKeyPattern(
                      wedding,
                      /(venue|location|restaurant|hall|place|lokacija)/i
                    );

                  return (
                  <TableRow key={weddingId || index}>
                    <TableCell>{formatCellValue(weddingId)}</TableCell>
                    <TableCell>
                      {confirmed === undefined
                        ? "-"
                        : confirmed
                          ? "Yes"
                          : "No"}
                    </TableCell>
                    <TableCell>{formatCellValue(bride)}</TableCell>
                    <TableCell>{formatCellValue(groom)}</TableCell>
                    <TableCell>{formatCellValue(date)}</TableCell>
                    <TableCell>{formatCellValue(venue)}</TableCell>
                  </TableRow>
                  );
                })}
                {weddings.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6}>Нема свадби.</TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

export default AdminWeddingsSection;
